/* ─── Included-item product drawer ──────────────────────────────────────────
   Drives the lightweight product preview drawer rendered by
   `snippets/included-item-drawer.liquid` for the "What's Included" rows.

   Behaviour mirrors the complete-set-perks "Learn More" modal: open from a
   `[data-included-drawer-trigger]` button, clone the per-variant gallery
   `<template>` into the live stage, page through slides via arrows/dots, and
   swap the gallery + variant label when a color swatch is picked. Read-only —
   no cart interaction.

   One delegated controller handles every drawer on the page; each drawer
   lazy-initializes its gallery on first open.
──────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var BODY_OPEN_CLASS = 'included-item-drawer-open';

  function getDrawerForTrigger(trigger) {
    var row = trigger.closest('[data-included-row]');
    return row ? row.querySelector('[data-included-drawer]') : null;
  }

  function setSlide(drawer, index) {
    var slides = drawer._slides || [];
    if (!slides.length) return;
    var clamped = Math.max(0, Math.min(slides.length - 1, index));
    drawer._currentSlide = clamped;
    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === clamped);
    });
    (drawer._dots || []).forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === clamped);
    });
    var prev = drawer.querySelector('[data-included-drawer-prev]');
    var next = drawer.querySelector('[data-included-drawer-next]');
    if (prev) prev.disabled = clamped <= 0;
    if (next) next.disabled = clamped >= slides.length - 1;
  }

  function rebuildDots(drawer) {
    var dotsEl = drawer.querySelector('[data-included-drawer-dots]');
    if (!dotsEl) return;
    dotsEl.innerHTML = '';
    drawer._dots = [];
    var count = drawer._slides ? drawer._slides.length : 0;
    dotsEl.hidden = count <= 1;
    if (count <= 1) return;

    for (var i = 0; i < count; i++) {
      (function (index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'included-item-drawer__dot';
        dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
        dot.addEventListener('click', function () {
          setSlide(drawer, index);
        });
        dotsEl.appendChild(dot);
        drawer._dots.push(dot);
      })(i);
    }
  }

  // Clone the `<template data-included-main="VID">` into the live stage. Falls
  // back to the drawer's initial variant template when the requested variant
  // has none (defensive — shouldn't happen).
  function loadGalleryForVariant(drawer, variantId) {
    var main = drawer.querySelector('[data-included-drawer-main]');
    if (!main) return;
    var vid = String(variantId);

    var tpl = drawer.querySelector('template[data-included-main="' + vid + '"]');
    if (!tpl) {
      var initial = main.getAttribute('data-included-initial-variant');
      tpl = drawer.querySelector('template[data-included-main="' + initial + '"]');
    }
    if (!tpl) return;

    main.innerHTML = '';
    main.appendChild(tpl.content.cloneNode(true));
    drawer._slides = Array.prototype.slice.call(
      main.querySelectorAll('[data-slide-index]')
    );

    rebuildDots(drawer);

    var hasMultiple = drawer._slides.length > 1;
    var prev = drawer.querySelector('[data-included-drawer-prev]');
    var next = drawer.querySelector('[data-included-drawer-next]');
    if (prev) prev.hidden = !hasMultiple;
    if (next) next.hidden = !hasMultiple;

    drawer._currentVariantId = vid;
    setSlide(drawer, 0);
  }

  function bindDrawer(drawer) {
    if (drawer._bound) return;
    drawer._bound = true;

    drawer.querySelectorAll('[data-included-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer(drawer);
      });
    });

    var prev = drawer.querySelector('[data-included-drawer-prev]');
    var next = drawer.querySelector('[data-included-drawer-next]');
    if (prev) {
      prev.addEventListener('click', function () {
        setSlide(drawer, drawer._currentSlide - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        setSlide(drawer, drawer._currentSlide + 1);
      });
    }

    var swatches = Array.prototype.slice.call(
      drawer.querySelectorAll('[data-included-drawer-swatch]')
    );
    var variantValue = drawer.querySelector('[data-included-drawer-variant-value]');
    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        var variantId = swatch.getAttribute('data-variant-id');
        var variantTitle = swatch.getAttribute('data-variant-title');
        swatches.forEach(function (s) {
          var isThis = s === swatch;
          s.classList.toggle('is-active', isThis);
          s.setAttribute('aria-checked', isThis ? 'true' : 'false');
        });
        if (variantValue && variantTitle) variantValue.textContent = variantTitle;
        if (variantId) loadGalleryForVariant(drawer, variantId);
      });
    });
  }

  // Reflect the given variant in the swatch grid + variant label.
  function syncVariantUi(drawer, variantId) {
    var vid = String(variantId);
    var match = null;
    drawer.querySelectorAll('[data-included-drawer-swatch]').forEach(function (s) {
      var isThis = s.getAttribute('data-variant-id') === vid;
      s.classList.toggle('is-active', isThis);
      s.setAttribute('aria-checked', isThis ? 'true' : 'false');
      if (isThis) match = s;
    });
    var variantValue = drawer.querySelector('[data-included-drawer-variant-value]');
    if (variantValue && match) {
      variantValue.textContent = match.getAttribute('data-variant-title') || variantValue.textContent;
    }
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    bindDrawer(drawer);

    // Reset to the initial (parent-color) variant on every open.
    var main = drawer.querySelector('[data-included-drawer-main]');
    var initial = main && main.getAttribute('data-included-initial-variant');
    if (initial) {
      syncVariantUi(drawer, initial);
      loadGalleryForVariant(drawer, initial);
    }

    drawer.hidden = false;
    // Force a reflow so the .is-open transition runs from the initial state.
    void drawer.offsetHeight;
    drawer.classList.add('is-open');
    document.body.classList.add(BODY_OPEN_CLASS);

    var closeBtn = drawer.querySelector('.included-item-drawer__close');
    if (closeBtn) {
      try {
        closeBtn.focus({ preventScroll: true });
      } catch (e) {
        closeBtn.focus();
      }
    }
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    var anyOpen = document.querySelector('.included-item-drawer.is-open');
    if (!anyOpen) document.body.classList.remove(BODY_OPEN_CLASS);
    setTimeout(function () {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 520);
    if (drawer._returnFocus) {
      try {
        drawer._returnFocus.focus({ preventScroll: true });
      } catch (e) {
        drawer._returnFocus.focus();
      }
      drawer._returnFocus = null;
    }
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-included-drawer-trigger]');
    if (!trigger) return;
    var drawer = getDrawerForTrigger(trigger);
    if (!drawer) return;
    e.preventDefault();
    drawer._returnFocus = trigger;
    openDrawer(drawer);
  });

  document.addEventListener('keyup', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.included-item-drawer.is-open');
    if (open) closeDrawer(open);
  });

  function handleize(value) {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Keep each drawer's default variant in sync with the chosen parent color,
  // so opening it previews the same color the row image shows.
  document.addEventListener('variant:swatch', function (e) {
    var value = e.detail && e.detail.value;
    if (!value) return;
    var color = handleize(value);
    document.querySelectorAll('[data-included-drawer]').forEach(function (drawer) {
      var main = drawer.querySelector('[data-included-drawer-main]');
      if (!main) return;
      var swatches = drawer.querySelectorAll('[data-included-drawer-swatch]');
      for (var i = 0; i < swatches.length; i++) {
        if (handleize(swatches[i].getAttribute('data-variant-title')) === color) {
          main.setAttribute(
            'data-included-initial-variant',
            swatches[i].getAttribute('data-variant-id')
          );
          break;
        }
      }
    });
  });
})();
